/**
 * TransformFX — "furacãozinho" de fumaça cinza para a transformação carro↔pessoa.
 *
 * Funil DENSO de puffs de fumaça (cinza escuro) numa hélice ascendente que gira
 * rápido em torno do avatar, com um BRILHO (flash aditivo) no auge da troca.
 * Aparece/some junto com a transformação. Reaproveitado via pool no EntityManager.
 */
import * as THREE from 'three';

let _puff = null;
let _glow = null;

function makeTex(stops) {
  const c = document.createElement('canvas');
  c.width = c.height = 64;
  const ctx = c.getContext('2d');
  const g = ctx.createRadialGradient(32, 32, 1, 32, 32, 31);
  for (const [o, col] of stops) g.addColorStop(o, col);
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 64, 64);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}
function puffTexture() {
  // fumaça CINZA-CLARA e densa (marcante, mas mais clara)
  return (_puff ||= makeTex([
    [0, 'rgba(196,200,202,0.82)'],
    [0.5, 'rgba(166,172,174,0.48)'],
    [1, 'rgba(150,156,158,0)']
  ]));
}
function glowTexture() {
  // brilho esverdeado (tema) — usado com blending aditivo
  return (_glow ||= makeTex([
    [0, 'rgba(224,255,236,0.95)'],
    [0.4, 'rgba(120,255,184,0.45)'],
    [1, 'rgba(70,255,150,0)']
  ]));
}

const smooth = (a, b, x) => {
  const t = Math.max(0, Math.min(1, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
};

export class TransformFX {
  constructor() {
    this.group = new THREE.Group();
    this.group.visible = false;
    // material próprio (opacidade independente por efeito), textura compartilhada
    this.mat = new THREE.SpriteMaterial({
      map: puffTexture(),
      transparent: true,
      depthWrite: false,
      opacity: 0
    });
    this.puffs = [];
    const N = 32; // bem denso → nuvem cheia, não bolinhas
    for (let i = 0; i < N; i++) {
      const f = i / (N - 1);
      const ang = i * 2.39996; // ângulo áureo → bem espalhado
      const r = (0.18 + Math.pow(f, 0.7) * 1.0) * (0.8 + 0.4 * Math.random());
      const s = new THREE.Sprite(this.mat);
      s.position.set(Math.cos(ang) * r, 0.1 + f * 3.1, Math.sin(ang) * r);
      const sz = 1.3 + f * 0.7 + Math.random() * 0.25; // grandes e sobrepostos
      s.scale.set(sz, sz, 1);
      s.userData = { ang, r, y: s.position.y };
      this.group.add(s);
      this.puffs.push(s);
    }
    // brilho central (flash) no auge — blending aditivo
    this.glowMat = new THREE.SpriteMaterial({
      map: glowTexture(),
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      opacity: 0
    });
    this.glow = new THREE.Sprite(this.glowMat);
    this.glow.position.set(0, 1.5, 0);
    this.glow.scale.set(3, 3, 1);
    this.group.add(this.glow);
    this._spin = Math.random() * 6.28;
  }

  /** dt: tempo do frame; t: progresso 0..1 da transformação; (x,z): posição base. */
  update(dt, t, x, z) {
    this.group.visible = true;
    this.group.position.set(x, 0, z);
    this._spin += dt * 14; // gira rápido (furacão)
    this.group.rotation.y = this._spin;
    // densa no auge da TROCA (esconde o swap em t≈0.5) e some logo depois, para
    // revelar o avatar emergindo/aterrissando.
    this.mat.opacity = Math.min(smooth(0, 0.16, t), 1 - smooth(0.5, 0.9, t));
    const breathe = 0.86 + 0.14 * Math.sin(this._spin * 0.8);
    this.group.scale.set(breathe, 1, breathe);
    for (const p of this.puffs) {
      p.position.y = p.userData.y + Math.sin(this._spin * 1.6 + p.userData.ang) * 0.12;
    }
    // brilho: pico no AUGE da troca (t≈0.5)
    const peak = Math.pow(Math.max(0, Math.sin(t * Math.PI)), 1.6);
    this.glowMat.opacity = peak * 0.95;
    const gs = 2.4 + peak * 2.2;
    this.glow.scale.set(gs, gs, 1);
  }

  hide() {
    this.group.visible = false;
    this.mat.opacity = 0;
    this.glowMat.opacity = 0;
  }
}
